import React, { useState, useEffect } from "react";

const DEFAULT_INTERVAL = 10000;

/**
 * Rotating sponsor banners for the expanded player.
 *
 * Reads the per-tenant sponsor list from config (`config.SPONSORS`) and cycles
 * through it on a timer, looping back to the start. Each banner is a clickable
 * link that opens the advertiser in a new tab. Renders nothing when there are
 * no sponsors, so the slot disappears entirely for tenants without ads.
 *
 * Props:
 *   items    - [{ src, href, alt }]  the banners to rotate (required)
 *   interval - ms between rotations  (optional, default 10000)
 */
const SponsorBanner = ({ items, interval }) => {
  const banners = Array.isArray(items) ? items.filter((i) => i && i.src) : [];
  const [index, setIndex] = useState(0);

  const rotateMs =
    typeof interval === "number" && interval > 0 ? interval : DEFAULT_INTERVAL;

  // Cycle to the next banner on a timer. Reset to a clean timer whenever the
  // list or interval changes, and don't bother running a timer for a single
  // banner (nothing to rotate to).
  useEffect(() => {
    if (banners.length <= 1) return undefined;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % banners.length);
    }, rotateMs);
    return () => clearInterval(id);
  }, [banners.length, rotateMs]);

  // Keep the index valid if the list shrinks underneath us.
  useEffect(() => {
    if (index >= banners.length) setIndex(0);
  }, [banners.length, index]);

  if (banners.length === 0) return null;

  const current = banners[Math.min(index, banners.length - 1)];

  return (
    <div className="expanded-ad-slot expanded-ad-slot--filled">
      <span className="expanded-ad-label">Advertisement</span>
      <a
        href={current.href}
        target="_blank"
        rel="noopener noreferrer"
        className="expanded-ad-link"
      >
        <img
          src={current.src}
          alt={current.alt || "Sponsor"}
          className="expanded-ad-image"
        />
      </a>
    </div>
  );
};

export default SponsorBanner;
